import type { ReactNode } from "react";
import Chart from "@/components/Chart";
import type { ChartVariant } from "@/components/Chart/Chart";
import M360MarkdownLink from "@/lib/M360MarkdownLink";
import Icon from "@/components/Icon";
import { normalizeChatDisplayText } from "../../normalizeChatDisplayText";
import { sanitizeCardMeta } from "../cardDisplayUtils";
import { AgentCardShell, AgentLinkChip, AgentList, type LinkItem, type ListItem } from "./shared";

type EntityCardProps = {
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  href?: string | null;
  imageUrl?: string | null;
  links?: LinkItem[] | null;
};

// —— Entity cards (tools: facilities, sections, jobs, events, …) ——

export function FacilityCard(props: EntityCardProps & { facilityId?: string | null; sectionName?: string | null }) {
  const subtitle = props.subtitle ?? props.sectionName;
  const meta = sanitizeCardMeta(props.meta) ?? undefined;
  return (
    <AgentCardShell
      eyebrow="Facility"
      icon="location_city"
      href={props.href}
      links={props.links}
      title={props.title}
      subtitle={subtitle}
      meta={meta}
      imageUrl={props.imageUrl}
    />
  );
}

export function SectionCard(props: EntityCardProps & { sectionId?: string | null }) {
  return <AgentCardShell eyebrow="Section" icon="groups" href={props.href} links={props.links} {...props} />;
}

export function JobPostCard(
  props: EntityCardProps & {
    jobPostId?: string | null;
    stage?: string | null;
    facilityName?: string | null;
    facilityHref?: string | null;
  }
) {
  const meta = sanitizeCardMeta(props.meta) ?? (props.stage ? props.stage : undefined);
  const facilityName = props.facilityName ?? props.subtitle;
  const subtitle = props.facilityName ? props.subtitle : undefined;
  return (
    <AgentCardShell
      eyebrow="Job post"
      icon="work"
      href={props.href}
      kind="job"
      links={props.links}
      title={props.title}
      subtitle={subtitle}
      facilityName={facilityName}
      facilityHref={props.facilityHref}
      meta={meta}
      imageUrl={props.imageUrl}
    />
  );
}

export function EventCard(
  props: EntityCardProps & {
    eventSlug?: string | null;
    dates?: string | null;
    facilityName?: string | null;
    facilityHref?: string | null;
    location?: string | null;
    status?: string | null;
    startsAt?: string | null;
    eventType?: string | null;
  }
) {
  const meta =
    sanitizeCardMeta(props.meta) ??
    ([props.status, props.eventType, props.dates ?? props.startsAt].filter(Boolean).join(" · ") || null);

  return (
    <AgentCardShell
      eyebrow="Event"
      icon="event"
      href={props.href}
      kind="event"
      links={props.links}
      title={props.title}
      subtitle={props.subtitle}
      facilityName={props.facilityName}
      facilityHref={props.facilityHref}
      location={props.location}
      meta={meta}
      imageUrl={props.imageUrl}
    />
  );
}

export function CommitteeCard(props: EntityCardProps & { role?: string | null; term?: string | null }) {
  const meta = props.meta ?? ([props.role, props.term].filter(Boolean).join(" · ") || undefined);
  return (
    <AgentCardShell
      eyebrow="Committee"
      icon="diversity_3"
      href={props.href}
      links={props.links}
      title={props.title}
      subtitle={props.subtitle}
      meta={meta || null}
      imageUrl={props.imageUrl}
    />
  );
}

export function RegistrationCard(
  props: EntityCardProps & { pgaId?: string | null; eventName?: string | null; status?: string | null }
) {
  const subtitle = props.subtitle ?? props.eventName;
  const meta =
    sanitizeCardMeta(props.meta) ?? (props.status ? props.status : props.pgaId ? `PGA ID ${props.pgaId}` : null);
  return (
    <AgentCardShell
      eyebrow="Registration"
      icon="how_to_reg"
      href={props.href}
      links={props.links}
      title={props.title}
      subtitle={subtitle}
      meta={meta}
      imageUrl={props.imageUrl}
    />
  );
}

export function OrderSummaryCard(
  props: EntityCardProps & { orderId?: string | null; total?: string | null; status?: string | null }
) {
  const meta = props.meta ?? ([props.total, props.status].filter(Boolean).join(" · ") || undefined);
  return (
    <AgentCardShell
      eyebrow="Commerce order"
      icon="receipt_long"
      href={props.href}
      links={props.links}
      title={props.title}
      subtitle={props.subtitle}
      meta={meta || null}
      imageUrl={props.imageUrl}
    />
  );
}

export function RosterActivityCard(
  props: EntityCardProps & { activityType?: string | null; effectiveDate?: string | null; sectionName?: string | null }
) {
  const subtitle = props.subtitle ?? props.sectionName;
  const meta = props.meta ?? ([props.activityType, props.effectiveDate].filter(Boolean).join(" · ") || undefined);
  return (
    <AgentCardShell
      eyebrow="Roster activity"
      icon="swap_horiz"
      href={props.href}
      links={props.links}
      title={props.title}
      subtitle={subtitle}
      meta={meta || null}
      imageUrl={props.imageUrl}
    />
  );
}

// —— CRM cards ——

export function NoteCard(props: {
  title: string;
  content?: string | null;
  priority?: string | null;
  visibility?: string | null;
  createdAt?: string | null;
  href?: string | null;
}) {
  const excerpt = props.content ? normalizeChatDisplayText(props.content).slice(0, 160) : undefined;
  const meta = [props.priority, props.visibility, props.createdAt].filter(Boolean).join(" · ");

  return (
    <article className="rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-4 shadow-web-1">
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sys-brand-tertiary-container text-sys-brand-on-tertiary-container"
          aria-hidden
        >
          <Icon name="sticky_note_2" size={20} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-label-medium text-sys-brand-primary">Staff note</p>
          <h3 className="text-title-small text-sys-brand-on-surface">
            {props.href ? (
              <M360MarkdownLink href={props.href} className="no-underline hover:text-sys-brand-primary">
                {normalizeChatDisplayText(props.title)}
              </M360MarkdownLink>
            ) : (
              normalizeChatDisplayText(props.title)
            )}
          </h3>
          {meta ? <p className="text-body-small text-sys-brand-on-surface-variant">{meta}</p> : null}
          {excerpt ? <p className="text-body-small text-sys-brand-on-surface">{excerpt}</p> : null}
        </div>
      </div>
    </article>
  );
}

export function NoteList(props: { title?: string | null; items: ListItem[] }) {
  return <AgentList title={props.title ?? "Notes"} items={props.items} emptyLabel="No notes" />;
}

export function InteractionList(props: { title?: string | null; items: ListItem[] }) {
  return <AgentList title={props.title ?? "Interactions"} items={props.items} emptyLabel="No interactions" />;
}

export function JobPostList(props: { title?: string | null; items: ListItem[] }) {
  return <AgentList title={props.title ?? "Job posts"} items={props.items} emptyLabel="No job posts" />;
}

export function EventList(props: { title?: string | null; items: ListItem[] }) {
  const safeItems = (Array.isArray(props.items) ? props.items : []).map((item) => ({
    ...item,
    kind: item.kind ?? "event"
  }));
  return <AgentList title={props.title ?? "Events"} items={safeItems} emptyLabel="No events" />;
}

export function TaskCard(props: {
  title: string;
  status?: string | null;
  dueOn?: string | null;
  assignee?: string | null;
  href?: string | null;
}) {
  const meta = [props.status, props.dueOn ? `Due ${props.dueOn}` : null, props.assignee].filter(Boolean).join(" · ");

  return (
    <article className="rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-4 shadow-web-1">
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sys-brand-primary-container text-sys-brand-on-primary-container"
          aria-hidden
        >
          <Icon name="task_alt" size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-label-medium text-sys-brand-primary">CRM task</p>
          <h3 className="text-title-small text-sys-brand-on-surface">
            {props.href ? (
              <M360MarkdownLink href={props.href} className="no-underline hover:text-sys-brand-primary">
                {normalizeChatDisplayText(props.title)}
              </M360MarkdownLink>
            ) : (
              normalizeChatDisplayText(props.title)
            )}
          </h3>
          {meta ? <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">{meta}</p> : null}
        </div>
      </div>
    </article>
  );
}

export function MembershipStatusCard(props: {
  program?: string | null;
  classification?: string | null;
  status?: string | null;
  bandDates?: string | null;
  section?: string | null;
  suspended?: boolean | null;
  href?: string | null;
  links?: LinkItem[] | null;
}) {
  const title = [props.program, props.classification].filter(Boolean).join(" / ") || "Membership";
  const subtitle = [props.status, props.section].filter(Boolean).join(" · ");
  const meta = props.bandDates ?? undefined;
  const eyebrow = props.suspended ? "Membership · Suspended" : "Membership";

  return (
    <AgentCardShell
      eyebrow={eyebrow}
      icon="card_membership"
      title={title}
      subtitle={subtitle || null}
      meta={meta ?? null}
      href={props.href}
      links={props.links}
    />
  );
}

// —— Metrics & summaries ——

export function CountSummaryCard(props: {
  count: number;
  resourceLabel: string;
  filters?: string[] | null;
  href?: string | null;
}) {
  return (
    <article className="rounded-2xl border border-sys-brand-outline-variant bg-gradient-to-br from-sys-brand-primary-container/40 to-sys-brand-surface-container-lowest p-5 shadow-web-2">
      <p className="text-label-medium text-sys-brand-primary">Count</p>
      <p className="mt-1 text-display-small font-medium text-sys-brand-on-surface">{props.count.toLocaleString()}</p>
      <p className="text-body-medium text-sys-brand-on-surface-variant">
        {normalizeChatDisplayText(props.resourceLabel)}
      </p>
      {props.filters?.length ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {props.filters.map((filter) => (
            <span
              key={filter}
              className="rounded-full bg-sys-brand-surface-container-high px-2.5 py-1 text-label-small text-sys-brand-on-surface-variant"
            >
              {filter}
            </span>
          ))}
        </div>
      ) : null}
      {props.href ? (
        <div className="mt-3">
          <AgentLinkChip label="View in M360" href={props.href} />
        </div>
      ) : null}
    </article>
  );
}

export function StatTile(props: {
  label: string;
  value: string;
  delta?: string | null;
  trend?: "up" | "down" | "flat" | null;
}) {
  const trendIcon = props.trend === "up" ? "trending_up" : props.trend === "down" ? "trending_down" : "remove";

  return (
    <div className="min-w-[8rem] flex-1 rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-4 shadow-web-1">
      <p className="text-label-medium text-sys-brand-on-surface-variant">{normalizeChatDisplayText(props.label)}</p>
      <p className="mt-1 text-headline-small text-sys-brand-on-surface">{props.value}</p>
      {props.delta ? (
        <p className="mt-1 flex items-center gap-1 text-body-small text-sys-brand-on-surface-variant">
          <span aria-hidden>
            <Icon name={trendIcon} size={16} />
          </span>
          {props.delta}
        </p>
      ) : null}
    </div>
  );
}

export function MetricRow({ children }: { children?: ReactNode }) {
  return <div className="flex flex-wrap gap-3">{children}</div>;
}

export function AlertBanner(props: {
  severity: "info" | "warning" | "error";
  title: string;
  message?: string | null;
  href?: string | null;
}) {
  const styles = {
    info: "border-sys-brand-primary/30 bg-sys-brand-primary-container/30",
    warning: "border-amber-500/40 bg-amber-50",
    error: "border-sys-brand-error/40 bg-sys-brand-error-container/20"
  }[props.severity];

  const icon = props.severity === "error" ? "error" : props.severity === "warning" ? "warning" : "info";

  return (
    <div className={`rounded-xl border p-4 ${styles}`} role={props.severity === "info" ? undefined : "alert"}>
      <div className="flex gap-3">
        <span aria-hidden>
          <Icon name={icon} size={22} className="shrink-0 text-sys-brand-on-surface" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-title-small text-sys-brand-on-surface">{normalizeChatDisplayText(props.title)}</p>
          {props.message ? (
            <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">
              {normalizeChatDisplayText(props.message)}
            </p>
          ) : null}
          {props.href ? (
            <div className="mt-2">
              <AgentLinkChip label="View details" href={props.href} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// —— PDR ——

export function PdrProgressCard(props: {
  earned: number;
  required: number;
  cycleLabel?: string | null;
  dueDate?: string | null;
  href?: string | null;
}) {
  const remaining = Math.max(props.required - props.earned, 0);
  const pct = props.required > 0 ? Math.round((props.earned / props.required) * 100) : 0;

  return (
    <article className="rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-4 shadow-web-2">
      <p className="text-label-medium text-sys-brand-primary">PDR progress</p>
      <p className="mt-1 text-title-large text-sys-brand-on-surface">
        {props.earned} / {props.required} credits
      </p>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-sys-brand-surface-container-high"
        role="progressbar"
        aria-valuenow={Math.min(pct, 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${props.earned} of ${props.required} PDR credits`}
      >
        <div
          className="h-full rounded-full bg-sys-brand-primary transition-all"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <p className="mt-2 text-body-small text-sys-brand-on-surface-variant">
        {remaining > 0 ? `${remaining} credits remaining` : "Requirement met"}
        {props.cycleLabel ? ` · ${props.cycleLabel}` : ""}
        {props.dueDate ? ` · Due ${props.dueDate}` : ""}
      </p>
      {props.href ? (
        <div className="mt-3">
          <AgentLinkChip label="View PDR" href={props.href} />
        </div>
      ) : null}
    </article>
  );
}

export function PdrCreditRow(props: {
  activity: string;
  credits: number;
  date?: string | null;
  category?: string | null;
}) {
  const meta = [props.date, props.category].filter(Boolean).join(" · ");

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-3 py-2">
      <div className="min-w-0">
        <p className="text-body-medium text-sys-brand-on-surface">{normalizeChatDisplayText(props.activity)}</p>
        {meta ? <p className="text-body-small text-sys-brand-on-surface-variant">{meta}</p> : null}
      </div>
      <span className="shrink-0 rounded-full bg-sys-brand-primary-container px-2.5 py-1 text-label-medium text-sys-brand-on-primary-container">
        +{props.credits}
      </span>
    </div>
  );
}

// —— Charts & tables ——

export type ChartDataPoint = { name: string; value: number; label?: number | null };

function coerceChartValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim().replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function normalizeChartData(data: ChartDataPoint[] | undefined): ChartDataPoint[] {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const normalized = data.map((row) => {
    const value = coerceChartValue(row?.value);
    if (value == null || !row?.name) {
      return null;
    }

    const coercedLabel = row.label == null ? null : coerceChartValue(row.label);
    if (row.label != null && coercedLabel == null) {
      return null;
    }

    return {
      name: String(row.name),
      value,
      ...(coercedLabel == null ? {} : { label: coercedLabel })
    };
  });

  if (normalized.some((row) => row === null)) {
    return [];
  }

  return normalized as ChartDataPoint[];
}

function toChartRows(data: ChartDataPoint[]) {
  return data.map(({ name, value, label }) => ({
    name,
    value,
    ...(label == null ? {} : { label })
  }));
}

function ChartEmptyState({ title }: { title: string }) {
  return (
    <div className="flex min-h-[12rem] w-full max-w-xl flex-col items-center justify-center rounded-lg border border-dashed border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-4 py-8 text-center">
      <p className="text-title-small text-sys-brand-on-surface">{normalizeChatDisplayText(title)}</p>
      <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">
        Chart data was missing or invalid. Ask again after refreshing the counts.
      </p>
    </div>
  );
}

export function ChartPanel(props: {
  variant: ChartVariant;
  title: string;
  subtitle?: string | null;
  data: ChartDataPoint[];
  total?: number | null;
  displayValue?: string | null;
  lines?: { dataKey: string; name: string; color: string }[] | null;
}) {
  const chartData = toChartRows(normalizeChartData(props.data));

  if (chartData.length === 0) {
    return <ChartEmptyState title={props.title} />;
  }

  if (props.variant === "line") {
    return (
      <div className="w-full max-w-xl">
        <Chart
          variant="line"
          data={chartData}
          lines={props.lines ?? [{ dataKey: "value", name: "Value", color: "#0053FF" }]}
          title={props.title}
          subtitle={props.subtitle ?? undefined}
        />
      </div>
    );
  }

  if (props.variant === "radar") {
    return (
      <div className="w-full max-w-xl">
        <Chart
          variant="radar"
          data={chartData}
          series={[{ dataKey: "value", name: "Score", color: "#0053FF" }]}
          angleKey="name"
          title={props.title}
          subtitle={props.subtitle ?? undefined}
        />
      </div>
    );
  }

  const coercedTotal = coerceChartValue(props.total);
  const total = coercedTotal ?? chartData.reduce((sum, row) => sum + row.value, 0);

  if (props.variant === "donut") {
    return (
      <div className="w-full max-w-xl">
        <Chart
          variant="donut"
          data={chartData}
          total={total}
          title={props.title}
          label={props.subtitle ?? props.title}
          displayValue={props.displayValue ?? String(total)}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl">
      <Chart variant="pie" data={chartData} totalValue={total} title={props.title} />
    </div>
  );
}

export function ChartDashboard({ title, children }: { title?: string | null; children?: ReactNode }) {
  return (
    <section className="space-y-4 rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-low/50 p-4">
      {title ? <h3 className="text-title-small text-sys-brand-on-surface">{normalizeChatDisplayText(title)}</h3> : null}
      <div className="grid gap-4 lg:grid-cols-2">{children}</div>
    </section>
  );
}

export type DataTableColumn = { key: string; label: string };
export type DataTableRow = Record<string, string>;

export function DataTable(props: {
  title?: string | null;
  caption?: string | null;
  columns: DataTableColumn[];
  rows: DataTableRow[];
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest shadow-web-1">
      {props.title ? (
        <div className="border-b border-sys-brand-outline-variant px-4 py-3">
          <h3 className="text-title-small text-sys-brand-on-surface">{normalizeChatDisplayText(props.title)}</h3>
          {props.caption ? (
            <p className="text-body-small text-sys-brand-on-surface-variant">
              {normalizeChatDisplayText(props.caption)}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[20rem] text-left text-body-small">
          <thead className="bg-sys-brand-surface-container-low text-label-medium text-sys-brand-on-surface-variant">
            <tr>
              {props.columns.map((col) => (
                <th key={col.key} className="px-4 py-2 font-medium">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-sys-brand-outline-variant">
            {props.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="text-sys-brand-on-surface">
                {props.columns.map((col) => (
                  <td key={col.key} className="px-4 py-2">
                    {row[col.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
